import { useEffect, useState } from 'react';
import './Tabs.css';

/*
 * Tab strip with proper roving-focus keyboard support.
 *
 * The AngularJS version wired tabs up with a jQuery click handler
 * (controllers.js:637) that called .tab('show') on Bootstrap, which left
 * them unreachable by keyboard.
 *
 * Panels render only when first opened and stay mounted afterwards, so the
 * heavier tabs do not all fetch on page load.
 */
export default function Tabs({ tabs, initialId, variant = 'primary', activeId, onChange }) {
	/* Controlled when activeId is supplied — /history drives its tabs from
	   the URL so they can be linked to and counted separately. */
	const controlled = activeId != null;
	const [internal, setInternal] = useState(initialId ?? tabs[0]?.id);
	const active = controlled ? activeId : internal;
	const [seen, setSeen] = useState(() => new Set([active]));

	/* Track panels opened via the URL, not only via a click. */
	useEffect(() => {
		setSeen((prev) => (prev.has(active) ? prev : new Set(prev).add(active)));
	}, [active]);

	const select = (id) => {
		if (!controlled) setInternal(id);
		setSeen((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
		onChange?.(id);
	};

	const onKeyDown = (event) => {
		const i = tabs.findIndex((t) => t.id === active);
		let next = null;
		if (event.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
		else if (event.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
		else if (event.key === 'Home') next = tabs[0];
		else if (event.key === 'End') next = tabs[tabs.length - 1];
		if (!next) return;
		event.preventDefault();
		select(next.id);
		document.getElementById(`tab-${next.id}`)?.focus();
	};

	return (
		<div className="tabs" data-variant={variant}>
			<div className="tabs__strip" role="tablist" onKeyDown={onKeyDown}>
				{tabs.map((tab) => (
					<button
						key={tab.id}
						id={`tab-${tab.id}`}
						role="tab"
						type="button"
						className="tabs__tab"
						aria-selected={tab.id === active}
						aria-controls={`panel-${tab.id}`}
						tabIndex={tab.id === active ? 0 : -1}
						onClick={() => select(tab.id)}
					>
						{tab.label}
					</button>
				))}
			</div>

			{tabs.map((tab) => (
				<div
					key={tab.id}
					id={`panel-${tab.id}`}
					role="tabpanel"
					aria-labelledby={`tab-${tab.id}`}
					hidden={tab.id !== active}
					className="tabs__panel"
				>
					{seen.has(tab.id) ? tab.render() : null}
				</div>
			))}
		</div>
	);
}
